import React, {Fragment, useMemo} from "react";
import {Layer, Rect, Stage} from "react-konva";
import {getStyledColor} from "../../../helpers/DrawHelper";
import {useIsTouchDevice} from "../../../hooks";
import Konva from "konva";
import KonvaEventObject = Konva.KonvaEventObject;
import Vector2d = Konva.Vector2d;

const SCROLL_BAR_HEIGHT = 12;
const TOUCH_SCROLL_BAR_HEIGHT = 24;
const TOUCH_MIN_THUMB_WIDTH = 32;
const CORNER_RADIUS = 4;
const MIN_THUMB_WIDTH = 2;

interface Props {
    width: number;
    position: number;
    max: number;
    screen: number;
    onScrollPositionChanged?: ((position: number) => void) | null;
}

export const HorizontalScrollbar: React.FC<Props> = ({
                                                         width,
                                                         position,
                                                         max,
                                                         screen,
                                                         onScrollPositionChanged = null
                                                     }: Props) => {
    const trackColor = getStyledColor("--scroll-bar-track");
    const thumbColor = getStyledColor("--scroll-bar-thumb");
    const thumbPosition = useMemo(() => width / max * position, [width, max, position]);
    // bigger bar and thumb for a finger
    const isTouch = useIsTouchDevice();
    const barHeight = isTouch ? TOUCH_SCROLL_BAR_HEIGHT : SCROLL_BAR_HEIGHT;
    const thumbWidth = useMemo(() => Math.max(isTouch ? TOUCH_MIN_THUMB_WIDTH : MIN_THUMB_WIDTH, width / max * screen), [width, max, screen, isTouch]);
    const dragBoundFunc = (pos: Vector2d) => {
        const scrollWidth = width - thumbWidth;
        const maxScroll = max - screen;
        const boundX = Math.round(Math.min(Math.max(0, pos.x), scrollWidth));
        const newPosition = Math.floor(boundX / scrollWidth * maxScroll);
        const newX = newPosition / maxScroll * scrollWidth;
        if (onScrollPositionChanged) {
            onScrollPositionChanged(newPosition);
        }
        return {
            x: newX,
            y: 0
        }
    };
    const scrollToTrackPoint = (x: number) => {
        if (onScrollPositionChanged) {
            const scrollWidth = width - thumbWidth;
            const maxScroll = max - screen;
            const clickPosition = Math.floor(x / scrollWidth * maxScroll);
            if (clickPosition < position) {
                onScrollPositionChanged(clickPosition);
            }
            if (clickPosition > position) {
                onScrollPositionChanged(clickPosition - screen);
            }
        }
    };
    const onTrackClick = (evt: KonvaEventObject<MouseEvent>) => scrollToTrackPoint(evt.evt.offsetX);
    const onTrackTap = (evt: KonvaEventObject<TouchEvent>) => {
        const pos = evt.target.getStage()?.getPointerPosition();
        if (pos) {
            scrollToTrackPoint(pos.x);
        }
    };
    return (
        <Fragment>
            <Stage width={width} height={barHeight}>
                <Layer>
                    <Rect x={0} y={0} width={width} height={barHeight} cornerRadius={CORNER_RADIUS}
                          fill={trackColor} onClick={onTrackClick} onTap={onTrackTap}/>
                    <Rect x={thumbPosition} y={0} width={thumbWidth} height={barHeight}
                          cornerRadius={CORNER_RADIUS}
                          fill={thumbColor} draggable={true} dragBoundFunc={dragBoundFunc}/>
                </Layer>
            </Stage>
        </Fragment>
    );
}